import React, { useState, useEffect } from "react";
import { connect } from "react-redux";
import { getDataFromAPI } from "../../../HandleAPICalls/actions";
import { useNavigate } from "react-router-dom";
import moment from "moment-timezone";
import UidDashboardComponent from "./component";

const UidDashboardContainer = (props) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [uidData, setUidData] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [totalCount, setTotalCount] = useState(0);
  const [createdUids, setCreatedUids] = useState([]);
  const [showSuccessDialog, setShowSuccessDialog] = useState(false);
  const [showErrorDialog, setShowErrorDialog] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  // QR Code states
  const [showBulkQRDialog, setShowBulkQRDialog] = useState(false);
  const [qrUids, setQrUids] = useState([]);
  const [pdfDownloading, setPdfDownloading] = useState(false);

  // Filter states - USING POPOVER ANCHOR ELEMENTS
  const [statusFilter, setStatusFilter] = useState('');
  const [mappingStateFilter, setMappingStateFilter] = useState('');
  const [statusFilterAnchorEl, setStatusFilterAnchorEl] = useState(null);
  const [mappingFilterAnchorEl, setMappingFilterAnchorEl] = useState(null);

  // Dialog states
  const [createUidOpen, setCreateUidOpen] = useState(false);
  const [apiLoading, setApiLoading] = useState(false);
  const [selectedUids, setSelectedUids] = useState(new Set());
  const [selectAll, setSelectAll] = useState(false);
  const [showListPage, setShowListPage] = useState(false);

  // Form data states
  const [newUidData, setNewUidData] = useState({ uid: '' });

  const pageSize = 50;

  // Load initial data on component mount
  useEffect(() => {
    loadUids(0);
  }, []);

  // 🔥 FIX: Function to load UIDs with search
  const loadUids = (pageNumber = 0, searchValue = searchTerm) => {
    setLoading(true);
    
    props.getDataFromAPI(
      `/partner/api/mappings/table?pageSize=${pageSize}&pageNumber=${pageNumber}&search=${encodeURIComponent(searchValue)}`,
      "GET",
      undefined,
      (response) => {
        if (response && Array.isArray(response)) {
          const uids = response || [];
          
          if (pageNumber === 0) {
            setUidData(uids);
          } else {
            setUidData(prevData => [...prevData, ...uids]);
          }
          
          setHasMore(uids.length === pageSize);
          setTotalCount(uids.length);
        } else {
          setError("Failed to fetch UID data");
        }
        setLoading(false);
        setCurrentPage(pageNumber);
      },
      (error) => {
        setError("Failed to fetch UID data");
        setLoading(false);
      }
    );
  };

  // Load next page of data
  const loadNextPage = () => {
    loadUids(currentPage + 1);
  };

  // 🔥 FIX: Handle search with debounce
  const handleSearch = (value) => {
    setSearchTerm(value);
    setCurrentPage(0);
    setSelectedUids(new Set());
    setSelectAll(false);
    // 🔥 Load with new search term
    loadUids(0, value);
  };

  // 🔥 FIX: Handle search with debounce (optional)
  const handleSearchDebounced = (value) => {
    setSearchTerm(value);
    // Clear selection
    setSelectedUids(new Set());
    setSelectAll(false);
    // Load with new search term after small delay
    setTimeout(() => {
      loadUids(0, value);
    }, 300);
  };

  // Handle individual checkbox selection
  const handleUidSelect = (uidId) => {
    const newSelected = new Set(selectedUids);
    if (newSelected.has(uidId)) {
      newSelected.delete(uidId);
    } else {
      newSelected.add(uidId);
    }
    setSelectedUids(newSelected);
  };

  // Handle select all checkbox
  const handleSelectAll = (newSelectedSet, isSelectingAll = false) => {
    setSelectedUids(newSelectedSet);
    setSelectAll(isSelectingAll);
  };

  // Check if a UID is selected
  const isUidSelected = (uidId) => {
    return selectedUids.has(uidId);
  };

  // Handle download selected UIDs
  const handleDownloadSelected = () => {
    if (selectedUids.size === 0) {
      showErrorPopup("Please select at least one UID to download");
      return;
    }

    const selectedUidData = filteredUids.filter(uid => selectedUids.has(uid.uid));

    const headers = ['S.No.', 'UID', 'VMID', 'VM Name', 'Last Update', 'Status', 'Mapping State'];
    
    const csvRows = selectedUidData.map((uid, index) => {
      const row = [
        index + 1,
        uid.uid || 'N/A',
        uid.vmId || 'N/A',
        uid.vmName || 'N/A',
        formatDate(uid.lastUpdatedAt),
        uid.status || 'N/A',
        uid.mappingState || 'N/A'
      ];
      
      return row.map(field => {
        if (typeof field === 'string' && (field.includes(',') || field.includes('"') || field.includes('\n'))) {
          return `"${field.replace(/"/g, '""')}"`;
        }
        return field;
      }).join(',');
    });

    const csvContent = [headers.join(','), ...csvRows].join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    const timestamp = new Date().toISOString().split('T')[0];

    link.href = url;
    link.setAttribute('download', `selected_uids_${timestamp}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // Handle view details
  const handleViewDetails = (uid) => {
    if (uid.mappingState === 'Mapped') {
      navigate(`/home/configuration/uid-unmap/${uid.uid}`);
    } else if (uid.mappingState === 'Unmapped') {
      navigate(`/home/configuration/uid-map/${uid.uid}`);
    } else {
      navigate(`/home/configuration/uid-details/${uid.uid}`);
    }
  };

  // Handle bulk upload
  const handleBulkUpload = () => {
    const selectedUidsArray = Array.from(selectedUids);
    navigate('/home/configuration/bulk-upload', { 
      state: { selectedUids: selectedUidsArray } 
    });
  };

  // Filter handlers
  const handleStatusFilterClick = (event) => {
    setStatusFilterAnchorEl(event.currentTarget);
  };

  const handleMappingFilterClick = (event) => {
    setMappingFilterAnchorEl(event.currentTarget);
  };

  const handleStatusFilterSelect = (value) => {
    setStatusFilter(value);
    setStatusFilterAnchorEl(null);
  };

  const handleMappingStateFilterSelect = (value) => {
    setMappingStateFilter(value);
    setMappingFilterAnchorEl(null);
  };

  const handleCloseStatusFilterPopup = () => {
    setStatusFilterAnchorEl(null);
  };

  const handleCloseMappingFilterPopup = () => {
    setMappingFilterAnchorEl(null);
  };

  const handleClearStatusFilter = () => {
    setStatusFilter('');
    setSelectedUids(new Set());
    setSelectAll(false);
  };

  const handleClearMappingFilter = () => {
    setMappingStateFilter('');
    setSelectedUids(new Set());
    setSelectAll(false);
  };

  // Apply filters to UIDs
  const filteredUids = uidData.filter(uid => {
    const statusMatch = !statusFilter || 
      (uid.status?.toString()?.toLowerCase() === statusFilter.toLowerCase());
    
    const mappingStateMatch = !mappingStateFilter || 
      (uid.mappingState?.toLowerCase() === mappingStateFilter.toLowerCase());
    
    return statusMatch && mappingStateMatch;
  });

  // Get unique status and mapping state values
  const statusOptions = [...new Set(uidData.map(uid => uid.status?.toString()).filter(Boolean))];
  const mappingStateOptions = [...new Set(uidData.map(uid => uid.mappingState).filter(Boolean))];

  // Create UID handlers
  const handleCreateUid = () => {
    setCreateUidOpen(true);
  };

  const handleCloseCreateUid = () => {
    setCreateUidOpen(false);
    setNewUidData({ uid: '' });
  };

  // Handle UID count input
  const handleUidCountChange = (value) => {
    const cleanValue = value.replace(/[^\d]/g, '');
    const trimmedValue = cleanValue.replace(/^0+/, '') || '';

    if (trimmedValue && Number(trimmedValue) > 50) {
      setNewUidData(prev => ({
        ...prev,
        uid: '50'
      }));
      return;
    }

    setNewUidData(prev => ({
      ...prev,
      uid: trimmedValue
    }));
  };

  const handleCreateUidSubmit = () => {
    if (!newUidData.uid.trim()) {
      showErrorPopup("Please enter the number of UIDs to create");
      return;
    }

    const count = parseInt(newUidData.uid);
    if (isNaN(count) || count <= 0) {
      showErrorPopup("Please enter a valid number (minimum 1)");
      return;
    }

    if (count > 50) {
      showErrorPopup("Maximum 50 UIDs can be created at once");
      return;
    }

    setApiLoading(true);
    onCreateUid(newUidData, (success) => {
      setApiLoading(false);
      if (success) {
        handleCloseCreateUid();
      }
    });
  };

  // Input change handlers
  const handleInputChange = (field, value) => {
    if (field === 'uid') {
      handleUidCountChange(value);
    } else {
      setNewUidData(prev => ({
        ...prev,
        [field]: value
      }));
    }
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return moment.utc(dateString).tz('Asia/Kolkata').format('DD/MM/YYYY, hh:mm:ss A');
    } catch (error) {
      console.error('Error formatting date:', error);
      return 'N/A';
    }
  };

  // List page handlers
  const handleBackFromList = () => {
    setShowListPage(false);
    setPdfDownloading(false);
  };

  // Handle download and redirect
  const handleDownloadAndRedirect = (uidsToDownload = createdUids) => {
    if (!Array.isArray(uidsToDownload) || uidsToDownload.length === 0) {
      showErrorPopup("No UIDs available for download");
      return;
    }

    downloadCreatedUidsCSV(uidsToDownload);
    setShowSuccessDialog(false);
    setShowListPage(false);
    setQrUids(uidsToDownload);
    setPdfDownloading(true);
    setShowBulkQRDialog(true);
  };

  // Download created UIDs as CSV
  const downloadCreatedUidsCSV = (uidsToDownload = createdUids) => {
    const headers = ['S.No.', 'UID', 'Status', 'Admin Name', 'Mapping State', 'Created At'];
    
    const csvRows = uidsToDownload.map((uid, index) => {
      const row = [
        index + 1,
        uid.uid || 'N/A',
        uid.status || 'N/A',
        uid.createdByName || 'N/A',
        uid.mappingState || 'N/A',
        formatDate(uid.createdAt)
      ];
      
      return row.map(field => {
        if (typeof field === 'string' && (field.includes(',') || field.includes('"') || field.includes('\n'))) {
          return `"${field.replace(/"/g, '""')}"`;
        }
        return field;
      }).join(',');
    });

    const csvContent = [headers.join(','), ...csvRows].join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    const timestamp = new Date().toISOString().split('T')[0];

    link.href = url;
    link.setAttribute('download', `created_uids_${timestamp}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // Handle PDF download from list page
  const handleStartPDFDownload = () => {
    if (createdUids.length === 0) {
      showErrorPopup("No UIDs available for download");
      return;
    }
    setShowListPage(false);
    setPdfDownloading(true);
    setQrUids(createdUids);
    setShowBulkQRDialog(true);
  };

  // Handle PDF download completion
  const handlePDFDownloadComplete = () => {
    setPdfDownloading(false);
    setShowBulkQRDialog(false);
    setQrUids([]);
  };

  // Create new UIDs
  const onCreateUid = (uidData, callback) => {
    const count = parseInt(uidData.uid);
    
    if (!count || count <= 0) {
      showErrorPopup("Please enter a valid count (minimum 1)");
      callback(false);
      return;
    }
    
    if (count > 50) {
      showErrorPopup("Maximum 50 UIDs can be created at once");
      callback(false);
      return;
    }

    const payload = {
      count: count
    };

    props.getDataFromAPI(
      "/partner/api/v2/uid/create",
      "POST",
      payload,
      (response) => {
        console.log("Create UID Response:", response);

        let createdUidsData = [];

        if (Array.isArray(response)) {
          createdUidsData = response;
        } else if (response && response.status && response.status.success === true) {
          if (Array.isArray(response.uids)) {
            createdUidsData = response.uids;
          } else if (Array.isArray(response.data)) {
            createdUidsData = response.data;
          } else if (Array.isArray(response.data?.uids)) {
            createdUidsData = response.data.uids;
          }
        } else if (response && typeof response === 'object') {
          if (Array.isArray(response.uids)) {
            createdUidsData = response.uids;
          } else if (Array.isArray(response.data)) {
            createdUidsData = response.data;
          } else if (response.uid || response.uidId || response.id) {
            createdUidsData = [response];
          }
        }

        console.log("Extracted UIDs Data:", createdUidsData);

        if (createdUidsData.length > 0) {
          setCreatedUids(createdUidsData);
          setShowSuccessDialog(false);
          callback(true);
          handleDownloadAndRedirect(createdUidsData);
          loadUids(0, searchTerm);
        } else {
          showErrorPopup("UIDs were created but no data returned");
          callback(false);
        }
      },
      (error) => {
        const errorMsg = error.message || "Failed to create UIDs";
        console.log("API Error:", errorMsg);
        showErrorPopup(errorMsg);
        callback(false);
      }
    );
  };

  // Show error popup
  const showErrorPopup = (message) => {
    setErrorMessage(message);
    setShowErrorDialog(true);
  };

  // Close error dialog
  const handleCloseErrorDialog = () => {
    setShowErrorDialog(false);
    setErrorMessage('');
  };

  // Close success dialog
  const handleCloseSuccessDialog = () => {
    setShowSuccessDialog(false);
    setCreatedUids([]);
  };

  return (
    <UidDashboardComponent
      uids={uidData}
      // loading={loading}
      error={error}
      searchTerm={searchTerm}
      currentPage={currentPage}
      hasMore={hasMore}
      totalItems={totalCount}
      
      showSuccessDialog={showSuccessDialog}
      createdUids={createdUids}
      showErrorDialog={showErrorDialog}
      errorMessage={errorMessage}
      createUidOpen={createUidOpen}
      apiLoading={apiLoading}
      showListPage={showListPage}
      
      showBulkQRDialog={showBulkQRDialog}
      qrUids={qrUids}
      pdfDownloading={pdfDownloading}
      setShowBulkQRDialog={setShowBulkQRDialog}
      
      statusFilter={statusFilter}
      mappingStateFilter={mappingStateFilter}
      statusFilterAnchorEl={statusFilterAnchorEl}
      mappingFilterAnchorEl={mappingFilterAnchorEl}
      statusOptions={statusOptions}
      mappingStateOptions={mappingStateOptions}
      
      selectedUids={selectedUids}
      selectAll={selectAll}
      
      newUidData={newUidData}
      
      navigate={navigate}
      
      // 🔥 FIX: Pass handleSearch function instead of just setSearchTerm
      setSearchTerm={handleSearch}
      onLoadNextPage={loadNextPage}
      handleCreateUid={handleCreateUid}
      handleCloseCreateUid={handleCloseCreateUid}
      handleCreateUidSubmit={handleCreateUidSubmit}
      handleBulkUpload={handleBulkUpload}
      handleInputChange={handleInputChange}
      handleUidSelect={handleUidSelect}
      handleSelectAll={handleSelectAll}
      isUidSelected={isUidSelected}
      handleDownloadSelected={handleDownloadSelected}
      handleViewDetails={handleViewDetails}
      handleStatusFilterClick={handleStatusFilterClick}
      handleMappingFilterClick={handleMappingFilterClick}
      handleStatusFilterSelect={handleStatusFilterSelect}
      handleMappingStateFilterSelect={handleMappingStateFilterSelect}
      handleCloseStatusFilterPopup={handleCloseStatusFilterPopup}
      handleCloseMappingFilterPopup={handleCloseMappingFilterPopup}
      handleClearStatusFilter={handleClearStatusFilter}
      handleClearMappingFilter={handleClearMappingFilter}
      handleBackFromList={handleBackFromList}
      onCloseSuccessDialog={handleCloseSuccessDialog}
      onCloseErrorDialog={handleCloseErrorDialog}
      onPDFDownloadComplete={handlePDFDownloadComplete}
      formatDate={formatDate}
      onDownloadAndRedirect={handleDownloadAndRedirect}
      filteredUids={filteredUids}
    />
  );
};

export default connect(null, { getDataFromAPI })(UidDashboardContainer);